import { useState, useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  BookOpen, Award, Users, DollarSign, Sparkles, CheckCircle2,
  ExternalLink, ArrowLeft, Play, Pause, Lock, MessageSquare, Download,
  Share2, ShieldCheck, ShieldAlert, Star, Bot, Code, HelpCircle, ChevronDown, ChevronRight,
  Bookmark, Settings, Volume2, VolumeX, Maximize2, FileText, Check, Clock,
  ArrowRight, ThumbsUp, Send, RefreshCw, FolderDown, EyeOff, QrCode, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const COURSE_DATA = {
  id: "llm-masterclass",
  title: "Learning Large Language Models: From Architecture to Autonomous Agents",
  shortTitle: "LLM Mastery: Architecture to Autonomous Agents",
  instructor: {
    name: "Dr. Andrew Lin",
    role: "Lead AI Research Scientist, Colibri AI Labs · ex-DeepMind",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&auto=format&fit=crop&q=80",
    bio: "Pioneered neural attention projections and published seminal work on parameter-efficient fine-tuning and agent reasoning loops."
  },
  rating: 4.9,
  ratingsCount: "2,418",
  studentsCount: "24,850",
  totalDuration: "6.5 h",
  totalFiles: 14,
  level: "Intermediate to Advanced",
  lastUpdated: "October 2026",
  language: "English [Auto-Captions]",
  coverVideoThumbnail: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1600&auto=format&fit=crop&q=80",
  about: `This flagship course offers a comprehensive, mathematically grounded yet practical dive into modern Generative AI and Large Language Model architectures. Starting from first principles, you will explore how attention mechanisms project queries, keys, and values across high-dimensional token embedding spaces to produce context-aware representations. You will implement self-attention from scratch in PyTorch, explore Rotary Positional Encodings (RoPE), optimize KV-caches for high-throughput inference, and fine-tune models using LoRA/QLoRA. Finally, you will bridge models with autonomous agent tools using structured JSON schemas and function calling loops.`,
  outcomes: [
    "Build scaled dot-product attention and multi-head projection layers from scratch in PyTorch.",
    "Implement Rotary Positional Encodings (RoPE) and optimize KV-cache memory during autoregressive generation.",
    "Perform Parameter-Efficient Fine-Tuning (LoRA & QLoRA) on open-weights foundation models.",
    "Architect autonomous multi-agent systems with tool execution loops, JSON schema constraints, and memory persistence."
  ],
  skills: [
    "Transformer Architectures",
    "Self-Attention Mechanics",
    "PyTorch",
    "LoRA / QLoRA Fine-Tuning",
    "KV-Cache Optimization",
    "Vector Embeddings",
    "Multi-Agent Tool Orchestration",
    "Prompt & Schema Engineering"
  ],
  modules: [
    {
      id: "mod-1",
      title: "Module 1: Transformer & Attention Mechanics",
      duration: "1 hr 45 min",
      lessons: [
        {
          id: "les-1",
          num: 1,
          title: "Introduction to Self-Attention Mechanics",
          duration: "18 min",
          completed: true,
          videoTime: "18:24",
          description: "Understanding how queries, keys, and values interact through vector dot-products to dynamically weight context.",
          code: `import torch\nimport torch.nn.functional as F\n\ndef self_attention(Q, K, V):\n    d_k = Q.size(-1)\n    scores = torch.matmul(Q, K.transpose(-2, -1)) / (d_k ** 0.5)\n    weights = F.softmax(scores, dim=-1)\n    return torch.matmul(weights, V)`
        },
        {
          id: "les-2",
          num: 2,
          title: "Scaled Dot-Product & Matrix Projections",
          duration: "24 min",
          completed: true,
          videoTime: "24:12",
          description: "Deep dive into gradient stabilization via 1/sqrt(d_k) scaling and linear projections W_q, W_k, W_v.",
          code: `class AttentionHead(torch.nn.Module):\n    def __init__(self, d_model, d_k):\n        super().__init__()\n        self.q = torch.nn.Linear(d_model, d_k, bias=False)\n        self.k = torch.nn.Linear(d_model, d_k, bias=False)\n        self.v = torch.nn.Linear(d_model, d_k, bias=False)\n    def forward(self, x):\n        return self_attention(self.q(x), self.k(x), self.v(x))`
        },
        {
          id: "les-3",
          num: 3,
          title: "Multi-Head Attention Subspace Projections",
          duration: "22 min",
          completed: true,
          videoTime: "22:50",
          description: "Projecting tokens across h independent attention subspaces and concatenating head representations.",
          code: `# Multi-head concatenation:\noutput = torch.cat([head(x) for head in self.heads], dim=-1)\nreturn self.out_proj(output)`
        },
        {
          id: "les-4",
          num: 4,
          title: "Positional Encodings & RoPE (Rotary Embeddings)",
          duration: "28 min",
          completed: false,
          videoTime: "28:15",
          description: "Relative vs absolute position representations, sinusoidal encodings, and rotary embeddings in LLaMA.",
          code: `def apply_rotary_emb(xq, xk, freqs_cis):\n    # Rotate queries and keys in 2D planes\n    xq_out = torch.view_as_complex(xq) * freqs_cis\n    xk_out = torch.view_as_complex(xk) * freqs_cis\n    return torch.view_as_real(xq_out), torch.view_as_real(xk_out)`
        },
      ]
    },
    {
      id: "mod-2",
      title: "Module 2: Tokenization & Embedding Vector Spaces",
      duration: "1 hr 30 min",
      lessons: [
        {
          id: "les-5",
          num: 5,
          title: "Byte-Pair Encoding (BPE) & SentencePiece",
          duration: "20 min",
          completed: false,
          videoTime: "20:30",
          description: "Greedy subword merge algorithms, vocabulary compression, and handling multilingual out-of-vocabulary tokens.",
          code: `# BPE Merge Loop\ndef get_stats(vocab):\n    pairs = collections.defaultdict(int)\n    for word, freq in vocab.items():\n        symbols = word.split()\n        for i in range(len(symbols)-1):\n            pairs[symbols[i], symbols[i+1]] += freq\n    return pairs`
        },
        {
          id: "les-6",
          num: 6,
          title: "Vector Spaces, Cosine Similarity & Dense Retrieval",
          duration: "25 min",
          completed: false,
          videoTime: "25:40",
          description: "Dense semantic embeddings, contrastive loss, bi-encoder architectures, and cosine distance search.",
          code: `sim = torch.cosine_similarity(query_emb, doc_emb, dim=-1)`
        },
      ]
    },
    {
      id: "mod-3",
      title: "Module 3: Fine-Tuning & Preference Optimization",
      duration: "1 hr 50 min",
      lessons: [
        {
          id: "les-7",
          num: 7,
          title: "Parameter-Efficient Fine-Tuning (LoRA & QLoRA)",
          duration: "35 min",
          completed: false,
          videoTime: "35:10",
          description: "Low-Rank Adaptation matrix decomposition W = W0 + B*A, 4-bit NormalFloat quantization, and memory efficiency.",
          code: `# LoRA Forward Pass\ndef lora_forward(x, W0, A, B, alpha=16, r=8):\n    base = torch.matmul(x, W0.T)\n    lora = torch.matmul(torch.matmul(x, A.T), B.T) * (alpha / r)\n    return base + lora`
        },
        {
          id: "les-8",
          num: 8,
          title: "Direct Preference Optimization (DPO) vs. RLHF",
          duration: "30 min",
          completed: false,
          videoTime: "30:05",
          description: "Implicit reward modeling from human pairwise rankings without training an unstable PPO reward model.",
          code: `loss = -F.logsigmoid(beta * (log_pi_w - log_ref_w) - beta * (log_pi_l - log_ref_l)).mean()`
        },
      ]
    },
    {
      id: "mod-4",
      title: "Module 4: Autonomous Agents & Tool Orchestration",
      duration: "1 hr 25 min",
      lessons: [
        {
          id: "les-9",
          num: 9,
          title: "Function Calling & JSON Schema Tool Definitions",
          duration: "26 min",
          completed: false,
          videoTime: "26:40",
          description: "Constraining model generation to valid JSON function parameters and executing external API links.",
          code: `tool_call = model.generate(prompt, tools=schema_list)\nif tool_call.function:\n    result = execute_sandbox(tool_call.function.name, tool_call.function.arguments)`
        },
        {
          id: "les-10",
          num: 10,
          title: "Multi-Agent Collaboration & Persistent Memory",
          duration: "38 min",
          completed: false,
          videoTime: "38:50",
          description: "Architecting specialist agents (planner, executor, verifier) with hierarchical reflection loops.",
          code: `orchestrator.dispatch([research_agent, code_agent, reviewer_agent])`
        },
      ]
    }
  ],
  supplements: [
    { id: "sup-1", title: "Transformer Self-Attention Cheat Sheet & Math Guide", type: "PDF", size: "4.2 MB", filename: "transformer_attention_cheatsheet.pdf" },
    { id: "sup-2", title: "PyTorch Attention & KV-Cache Implementation Notebook", type: "Jupyter Notebook", size: "1.8 MB", filename: "scaled_attention_kv_cache.ipynb" },
    { id: "sup-3", title: "Autonomous Multi-Agent JSON Tool Call Schemas", type: "JSON Schema", size: "340 KB", filename: "agent_tool_schemas.json" },
  ],
  recommendations: [
    {
      id: "rec-1",
      title: "Multimodal Generative Vision & Omni Models",
      instructor: "Dr. Sarah Lin",
      rating: 4.9,
      students: "12,400",
      hours: "5.2 hrs",
      cover: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&auto=format&fit=crop&q=80",
      tag: "Next in Specialization"
    },
    {
      id: "rec-2",
      title: "High-Performance RAG & Vector Database Systems",
      instructor: "Marcus Vance",
      rating: 4.8,
      students: "8,920",
      hours: "4.0 hrs",
      cover: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&auto=format&fit=crop&q=80",
      tag: "Recommended by Students"
    }
  ]
};

const SECONDARY_COURSES = [
  {
    id: "growth-201",
    title: "Omnichannel Growth & Marketing OS Masterclass",
    instructor: "LaunchDeck Strategy Team",
    progress: 100,
    modules: 6,
    totalHours: "4.2 hrs",
    cover: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&auto=format&fit=crop&q=80",
    description: "Step-by-step systems for automated lead acquisition, multi-tier sales pipelines, and high-ticket retainer closing.",
  },
  {
    id: "media-301",
    title: "High-Converting Visual Media & Colibri Studio",
    instructor: "Elena Rostova",
    progress: 75,
    modules: 4,
    totalHours: "3.5 hrs",
    cover: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&auto=format&fit=crop&q=80",
    description: "Produce commercial viral assets, organize client photo galleries, and automate cross-platform publishing.",
  }
];

// ── Pre-Completion Knowledge Verification Quizzes (User Requirement) ──
const LESSON_QUIZZES = {
  "les-1": {
    question: "In self-attention mechanics, how is the token attention matrix computed before softmax normalization?",
    options: [
      "By adding Query and Key vectors linearly.",
      "By computing the scaled dot-product: (Q · Kᵀ) / √d_k.",
      "By applying ReLU activation directly to the Value vectors.",
      "By passing the input embeddings through an unscaled recurrent cell."
    ],
    correctIndex: 1,
    explanation: "Self-attention computes pairwise token compatibility via dot product (Q · Kᵀ) scaled by √d_k to prevent gradients from vanishing in extreme softmax regions."
  },
  "les-2": {
    question: "Why is the 1/√d_k factor critical in scaled dot-product attention?",
    options: [
      "It compresses the embedding dimensions by half.",
      "It prevents large dot products from pushing softmax into flat regions with vanishing gradients.",
      "It ensures that weights sum to exactly zero.",
      "It eliminates the need for feedforward layers."
    ],
    correctIndex: 1,
    explanation: "For large d_k, dot products grow large in magnitude, pushing softmax into saturated regions with tiny gradients. Scaling by 1/√d_k keeps variance near 1."
  },
  "les-3": {
    question: "What is the primary architectural advantage of Multi-Head Attention over a single large attention head?",
    options: [
      "It uses fewer total parameters than a single head.",
      "It allows the model to jointly attend to information from different representation subspaces at different positions.",
      "It eliminates residual skip connections entirely.",
      "It enforces strictly causal token masking in bidirectional encoders."
    ],
    correctIndex: 1,
    explanation: "Multi-Head Attention projects queries, keys, and values into h different representation subspaces, letting the model simultaneously capture syntax, semantics, and relative distance."
  },
  "les-4": {
    question: "How do Rotary Positional Embeddings (RoPE) represent relative distance compared to traditional sinusoidal encodings?",
    options: [
      "They add fixed position scalars to the embedding layer only.",
      "They rotate Query and Key projections in 2D planes, preserving relative distance in the inner product without adding trainable parameters.",
      "They discard sequence ordering to reduce memory usage.",
      "They replace dot-product attention with convolutional feature maps."
    ],
    correctIndex: 1,
    explanation: "RoPE incorporates relative distance via 2D rotation matrices applied directly to queries and keys: <R(m)q, R(n)k> = qᵀ R(n-m) k, naturally preserving relative position across long horizons."
  }
};

const getLessonQuiz = (les) => {
  if (les && LESSON_QUIZZES[les.id]) return LESSON_QUIZZES[les.id];
  return {
    question: `Key architectural concept check for "${les?.title || 'Lesson'}": Which design principle ensures stable convergence in deep transformer backbones?`,
    options: [
      "Stabilizing activation variance with LayerNorm / RMSNorm and residual skip connections.",
      "Removing all non-linear activation functions.",
      "Setting all learning rates to a constant unadjusted 1.0.",
      "Eliminating projection weights in the feedforward network."
    ],
    correctIndex: 0,
    explanation: "Pre-LayerNorm / RMSNorm combined with residual skip connections ensures stable forward and backward signal propagation across deep transformer layers."
  };
};

export default function ClientPortal() {
  const { id } = useParams();
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState("player"); // "player" | "catalog" | "community" | "credentials"
  const [activeLessonId, setActiveLessonId] = useState("les-4"); // Currently on lesson 4
  const [activeTab, setActiveTab] = useState("overview"); // overview | outcomes | qa | notes | announcements
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState("1.0x");
  const [progressSec, setProgressSec] = useState(145); // simulated 2:25 into video
  const [completedLessons, setCompletedLessons] = useState(["les-1", "les-2", "les-3"]);

  // ── DRM & Anti-Download Video Protection State (Course Strict Security) ──
  const canvasRef = useRef(null);
  const [drmTicket, setDrmTicket] = useState(null);
  const [devToolsActive, setDevToolsActive] = useState(false);
  const watermarkPos = useRef({ x: 100, y: 150, vx: 0.9, vy: 0.7 });
  const animFrameRef = useRef(null);

  // ── Quiz & Automatic 2-Year Certificate State ──
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizLesson, setQuizLesson] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [quizError, setQuizError] = useState("");
  const [certModalOpen, setCertModalOpen] = useState(false);
  const [issuedCert, setIssuedCert] = useState(null);

  // AI Tutor Q&A State
  const [qaMessages, setQaMessages] = useState([
    {
      id: "m-1",
      sender: "Dr. Andrew Lin (Instructor)",
      role: "instructor",
      time: "2 days ago",
      text: "Welcome to Lesson 1.4! Pay close attention to how Rotary Positional Embeddings (RoPE) rotate query and key vectors in 2-dimensional planes instead of simply adding position vectors. This guarantees relative distance awareness during dot-product attention."
    },
    {
      id: "m-2",
      sender: "You",
      role: "student",
      time: "Yesterday",
      text: "Why is RoPE preferred over Sinusoidal embeddings in modern architectures like LLaMA and Mistral?"
    },
    {
      id: "m-3",
      sender: "AI Course Tutor (Gemini)",
      role: "assistant",
      time: "Yesterday",
      text: "Great question! Sinusoidal embeddings add position information directly to token embeddings at the input layer, but this information gets distorted across multiple deep non-linear layers. RoPE applies rotation directly at each attention layer to queries and keys: <R(m)q, R(n)k> = q^T R(n-m) k. Because the inner product depends strictly on (n - m), the model naturally captures relative distance with zero added trainable parameters!"
    }
  ]);
  const [userQuestion, setUserQuestion] = useState("");
  const [askingAi, setAskingAi] = useState(false);

  // Flattened lessons list
  const allLessons = COURSE_DATA.modules.flatMap(m => m.lessons);
  const currentLesson = allLessons.find(l => l.id === activeLessonId) || allLessons[3];

  // ── 1. Fetch Cryptographic DRM Token on Active Lesson Switch ──
  useEffect(() => {
    const studentIdentifier = user?.email || user?.id || "student_colibri_vip";
    api.get(`/portal/courses/drm/ticket?lesson_id=${activeLessonId}&student_id=${encodeURIComponent(studentIdentifier)}`)
      .then(res => {
        if (res.data?.ok) {
          setDrmTicket(res.data);
        }
      })
      .catch(() => {
        // Fallback offline DRM token
        setDrmTicket({
          anti_tamper_watermark: {
            session_id: `DRM-${Math.random().toString(36).substring(2, 9).toUpperCase()}`,
            timestamp: new Date().toISOString()
          }
        });
      });
  }, [activeLessonId, user]);

  // ── 2. Anti-DevTools & Inspect Shortcut Interceptor (Course Only) ──
  useEffect(() => {
    if (viewMode !== "player") return;

    const handleKeyDown = (e) => {
      // Intercept F12
      if (e.key === "F12") {
        e.preventDefault();
        e.stopPropagation();
        toast.error("🔒 Security Notice: Developer tools and source inspection are disabled in protected course sessions.");
        return;
      }
      // Intercept Ctrl+Shift+I / Cmd+Option+I / Ctrl+Shift+J / Cmd+Option+J / Ctrl+Shift+C / Cmd+Option+C
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && ["I", "i", "J", "j", "C", "c"].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        toast.error("🔒 Security Notice: Inspection shortcuts are restricted for copyrighted lecture video.");
        return;
      }
      // Intercept Ctrl+U (View Source) / Ctrl+S (Save Webpage)
      if ((e.ctrlKey || e.metaKey) && ["u", "U", "s", "S"].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        toast.error("🔒 Security Notice: Source downloading is prohibited for authenticated course curriculum.");
        return;
      }
    };

    // DevTools open heuristic detector
    const detectDevTools = () => {
      const threshold = 160;
      const widthDiff = window.outerWidth - window.innerWidth > threshold;
      const heightDiff = window.outerHeight - window.innerHeight > threshold;
      if (widthDiff || heightDiff) {
        setDevToolsActive(true);
        setIsPlaying(false);
      } else {
        setDevToolsActive(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("resize", detectDevTools);
    const dtInterval = setInterval(detectDevTools, 1500);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("resize", detectDevTools);
      clearInterval(dtInterval);
    };
  }, [viewMode]);

  // ── 3. High-Security HTML5 Canvas Video Rendering Engine ──
  // Renders dynamic lecture visuals & burned forensic watermark directly in canvas pixels
  // NO <video> tag, NO .mp4 file, NO source element exists in the DOM!
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let step = progressSec * 60;

    const renderFrame = () => {
      const w = canvas.width;
      const h = canvas.height;

      // 1. Dark futuristic canvas backdrop
      const bgGrad = ctx.createLinearGradient(0, 0, w, h);
      bgGrad.addColorStop(0, "#080c14");
      bgGrad.addColorStop(0.5, "#0b1329");
      bgGrad.addColorStop(1, "#070a12");
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, w, h);

      // 2. Technical subtle coordinate grid
      ctx.strokeStyle = "rgba(79, 70, 229, 0.08)";
      ctx.lineWidth = 1;
      const gridSize = 40;
      for (let x = 0; x < w; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = 0; y < h; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      // 3. Lesson Lecture Content: Animated Neural Attention Mechanism & RoPE Rotation
      const t = step * 0.02;

      // Draw Transformer Attention Matrix Core
      const centerX = w * 0.44;
      const centerY = h * 0.50;
      const radius = 170;

      // Central core pulsing circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, 55 + Math.sin(t * 1.5) * 6, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(99, 102, 241, 0.16)";
      ctx.fill();
      ctx.strokeStyle = "rgba(129, 140, 248, 0.6)";
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 15px monospace";
      ctx.textAlign = "center";
      ctx.fillText("QKᵀ / √dₖ", centerX, centerY - 6);
      ctx.fillStyle = "#a5b4fc";
      ctx.font = "12px monospace";
      ctx.fillText("Softmax Matrix", centerX, centerY + 14);

      // Draw 6 Peripheral Attention Head Nodes
      const nodes = [
        { label: "Token 1 [CLS]", color: "#60a5fa" },
        { label: "Token 2 [Embed]", color: "#34d399" },
        { label: "Token 3 [Query]", color: "#a78bfa" },
        { label: "Token 4 [Key]", color: "#f472b6" },
        { label: "Token 5 [Value]", color: "#fbbf24" },
        { label: "Token 6 [RoPE]", color: "#38bdf8" },
      ];

      nodes.forEach((node, i) => {
        const angle = (i * Math.PI * 2) / nodes.length + t * 0.2;
        const nx = centerX + Math.cos(angle) * radius;
        const ny = centerY + Math.sin(angle) * radius;

        // Dynamic glowing connecting line with oscillating weight
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(nx, ny);
        const alpha = 0.25 + 0.2 * Math.cos(t * 2 + i);
        ctx.strokeStyle = `rgba(165, 180, 252, ${alpha})`;
        ctx.lineWidth = 1.5 + Math.sin(t + i);
        ctx.stroke();

        // Node circle
        ctx.beginPath();
        ctx.arc(nx, ny, 22, 0, Math.PI * 2);
        ctx.fillStyle = "#1e1b4b";
        ctx.fill();
        ctx.strokeStyle = node.color;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Node label
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 11px sans-serif";
        ctx.fillText(node.label, nx, ny + 34);
      });

      // 4. RoPE Geometric Coordinate Planes & Vectors
      const ropeX = w * 0.82;
      const ropeY = h * 0.46;
      ctx.strokeStyle = "rgba(148, 163, 184, 0.3)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(ropeX - 80, ropeY);
      ctx.lineTo(ropeX + 80, ropeY);
      ctx.moveTo(ropeX, ropeY - 80);
      ctx.lineTo(ropeX, ropeY + 80);
      ctx.stroke();

      // Rotating query vector
      const ropeAngle = t * 1.2;
      const qVx = ropeX + Math.cos(ropeAngle) * 65;
      const qVy = ropeY + Math.sin(ropeAngle) * 65;
      ctx.beginPath();
      ctx.moveTo(ropeX, ropeY);
      ctx.lineTo(qVx, qVy);
      ctx.strokeStyle = "#38bdf8";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      ctx.fillStyle = "#38bdf8";
      ctx.font = "bold 11px monospace";
      ctx.fillText("R(m)q vector", qVx + 10, qVy);

      // Rotating key vector
      const kAngle = ropeAngle - 0.75;
      const kVx = ropeX + Math.cos(kAngle) * 65;
      const kVy = ropeY + Math.sin(kAngle) * 65;
      ctx.beginPath();
      ctx.moveTo(ropeX, ropeY);
      ctx.lineTo(kVx, kVy);
      ctx.strokeStyle = "#f472b6";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      ctx.fillStyle = "#f472b6";
      ctx.font = "bold 11px monospace";
      ctx.fillText("R(n)k vector", kVx + 10, kVy);

      // 5. Audio Waveform Oscilloscope (Simulated Instructor Voice)
      const waveY = h - 65;
      ctx.strokeStyle = isPlaying ? "rgba(56, 189, 248, 0.7)" : "rgba(148, 163, 184, 0.3)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let x = w * 0.15; x < w * 0.75; x += 6) {
        const freq = isPlaying ? Math.sin((x * 0.05) + t * 4) * Math.cos(x * 0.02 + t * 2) * 16 : 0;
        ctx.moveTo(x, waveY);
        ctx.lineTo(x, waveY - freq);
      }
      ctx.stroke();

      // 6. Instructor Picture-in-Picture Preview in Top-Right
      const pipW = 210;
      const pipH = 110;
      const pipX = w - pipW - 30;
      const pipY = 30;

      ctx.fillStyle = "rgba(15, 23, 42, 0.88)";
      ctx.strokeStyle = "rgba(99, 102, 241, 0.4)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(pipX, pipY, pipW, pipH, 12);
      ctx.fill();
      ctx.stroke();

      // Instructor badge text
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 12px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(COURSE_DATA.instructor.name, pipX + 16, pipY + 32);

      ctx.fillStyle = "#94a3b8";
      ctx.font = "11px sans-serif";
      ctx.fillText("AI Faculty · Colibri Labs", pipX + 16, pipY + 50);

      // Speaking status dot
      ctx.beginPath();
      ctx.arc(pipX + 22, pipY + 80, 5, 0, Math.PI * 2);
      ctx.fillStyle = isPlaying ? "#10b981" : "#64748b";
      ctx.fill();

      ctx.fillStyle = isPlaying ? "#34d399" : "#94a3b8";
      ctx.font = "bold 11px monospace";
      ctx.fillText(isPlaying ? "MIC LIVE · LECTURING" : "PAUSED", pipX + 34, pipY + 84);

      // 7. Lecture Header Bar Inside Canvas
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 18px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(`${currentLesson.title}`, 36, 48);

      ctx.fillStyle = "#818cf8";
      ctx.font = "bold 12px monospace";
      ctx.fillText("SECTION 1.4 · MATHEMATICAL DERIVATION & TENSOR PROJECTIONS", 36, 72);

      // ── 8. CRITICAL: DYNAMIC FORENSIC ANTI-PIRACY WATERMARK ──
      // Bounces continuously across the canvas at 60fps
      // If someone screen records or takes photos, the student's legal identity is permanently burned in!
      const wm = watermarkPos.current;
      wm.x += wm.vx;
      wm.y += wm.vy;
      if (wm.x > w - 460 || wm.x < 30) wm.vx *= -1;
      if (wm.y > h - 80 || wm.y < 120) wm.vy *= -1;

      const studentName = user?.name || "Verified Student";
      const studentEmail = user?.email || "colibri-student@launchdeck.io";
      const sessId = drmTicket?.anti_tamper_watermark?.session_id || "DRM-8921-ENCR";

      ctx.save();
      ctx.fillStyle = "rgba(255, 255, 255, 0.26)";
      ctx.font = "bold 12px monospace";
      ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
      ctx.shadowBlur = 4;
      ctx.fillText(`🔒 CONFIDENTIAL · STUDENT: ${studentName.toUpperCase()} · ${studentEmail} · SESS: ${sessId}`, wm.x, wm.y);
      ctx.font = "10px monospace";
      ctx.fillText("UNAUTHORIZED RECORDING OR DISTRIBUTION IS STRICTLY MONITORED & PROHIBITED", wm.x, wm.y + 16);
      ctx.restore();

      if (isPlaying) {
        step += playbackSpeed === "2.0x" ? 2 : playbackSpeed === "1.5x" ? 1.5 : playbackSpeed === "1.25x" ? 1.25 : 1;
      }
      animFrameRef.current = requestAnimationFrame(renderFrame);
    };

    animFrameRef.current = requestAnimationFrame(renderFrame);

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isPlaying, playbackSpeed, currentLesson, user, drmTicket]);

  // ── 4. Progress Sec Ticker When Playing ──
  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      setProgressSec(prev => {
        if (prev >= 1100) return 0;
        return prev + 1;
      });
    }, 1000 / (playbackSpeed === "2.0x" ? 2 : playbackSpeed === "1.5x" ? 1.5 : playbackSpeed === "1.25x" ? 1.25 : 1));
    return () => clearInterval(interval);
  }, [isPlaying, playbackSpeed]);

  // ── Interactive Knowledge Test Before Marking Complete (User Requirement) ──
  const handleToggleComplete = (lesId, e) => {
    e.stopPropagation();
    if (completedLessons.includes(lesId)) {
      setCompletedLessons(completedLessons.filter(id => id !== lesId));
      toast.info("Lesson marked incomplete");
    } else {
      const targetLes = allLessons.find(l => l.id === lesId) || currentLesson;
      setQuizLesson(targetLes);
      setSelectedOption(null);
      setQuizError("");
      setQuizOpen(true);
    }
  };

  const handleVerifyQuiz = () => {
    if (selectedOption === null) {
      setQuizError("Please select an answer to verify your understanding.");
      return;
    }
    const q = getLessonQuiz(quizLesson);
    if (selectedOption !== q.correctIndex) {
      setQuizError(`Incorrect answer. Hint: ${q.explanation}`);
      return;
    }

    const newCompleted = Array.from(new Set([...completedLessons, quizLesson.id]));
    setCompletedLessons(newCompleted);
    setQuizOpen(false);
    toast.success(`Knowledge Verified! "${quizLesson.title}" marked complete! +50 XP`);

    // Automatic Certificate Generation upon full course/module completion
    if (newCompleted.length >= allLessons.length) {
      const certId = `CERT-LLM-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`;
      const issueDate = new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      const expiryDate = new Date(Date.now() + 730 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
      const verificationUrl = `https://deck.weddingsthyzfoto.com/verify/${certId}`;
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(verificationUrl)}`;

      const newCert = {
        id: certId,
        title: COURSE_DATA.title,
        recipient: user?.name || "Sarah Jenkins",
        recipient_email: user?.email || "sarah@apexculinary.com",
        issue_date: issueDate,
        expiry_date: expiryDate,
        validity: `2 Years (Valid through ${expiryDate})`,
        status: "Active & Verified",
        verification: verificationUrl,
        qr_code: qrCodeUrl,
        skills: COURSE_DATA.skills,
        issuer: "Colibri AI Labs & LaunchDeck Global Professional Education",
        sha256_hash: "A7E294B1842C0F19"
      };

      setIssuedCert(newCert);
      setCertModalOpen(true);
      toast.success("🎉 Specialization Complete! Official Verified Certificate Issued.");

      api.post("/portal/certificates/auto-issue", {
        course_id: COURSE_DATA.id,
        course_title: COURSE_DATA.title,
        recipient_name: user?.name || "Sarah Jenkins",
        recipient_email: user?.email || "sarah@apexculinary.com",
        skills_learned: COURSE_DATA.skills
      }).catch(() => {});
    }
  };

  const handleSelectLesson = (les) => {
    setActiveLessonId(les.id);
    setIsPlaying(true);
    toast.success(`Switched to: ${les.title}`);
  };

  const handleAskTutor = async (e) => {
    e.preventDefault();
    if (!userQuestion.trim()) return;

    const newMsg = {
      id: `m-${Date.now()}`,
      sender: "You",
      role: "student",
      time: "Just now",
      text: userQuestion
    };
    setQaMessages(prev => [...prev, newMsg]);
    const qText = userQuestion;
    setUserQuestion("");
    setAskingAi(true);

    try {
      const res = await api.post("/ai/social/caption", {
        topic: `Technical question about LLM lesson "${currentLesson.title}": "${qText}". Explain rigorously with mathematical clarity for an AI engineer student.`,
        platform: "Course Tutor",
        tone: "academic, pedagogical, clear"
      });
      const aiReply = res.data?.caption || `In relation to ${currentLesson.title}: When training transformer attention heads, ${qText} is resolved by ensuring gradient flow remains stable across residual skip connections.`;
      setQaMessages(prev => [
        ...prev,
        {
          id: `m-${Date.now() + 1}`,
          sender: "AI Course Tutor (Gemini)",
          role: "assistant",
          time: "Just now",
          text: aiReply
        }
      ]);
    } catch {
      setTimeout(() => {
        setQaMessages(prev => [
          ...prev,
          {
            id: `m-${Date.now() + 1}`,
            sender: "AI Course Tutor (Gemini)",
            role: "assistant",
            time: "Just now",
            text: `In "${currentLesson.title}", this operation guarantees that the inner product between query and key vectors scales inversely with sequence distance, enabling the model to retain context over 128k+ token horizons.`
          }
        ]);
      }, 700);
    } finally {
      setAskingAi(false);
    }
  };

  const handleDownloadSupplement = (file) => {
    toast.success(`Downloading ${file.filename} (${file.size})...`);
    const blob = new Blob([`LaunchDeck Academy · ${COURSE_DATA.title}\nResource: ${file.title}\nFormat: ${file.type}`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const percentComplete = Math.round((completedLessons.length / allLessons.length) * 100);

  return (
    <div className="min-h-screen bg-[#F4F6FA] text-slate-900 flex flex-col font-sans antialiased">
      {/* ── Top Header Bar (Matching SlothUI / Coursera Design) ── */}
      <header className="h-16 bg-white border-b border-slate-200/90 px-4 sm:px-8 flex items-center justify-between sticky top-0 z-40 shadow-xs">
        {/* Left: Brand + Course Title Breadcrumb */}
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/" className="flex items-center gap-2 shrink-0 group">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center font-bold text-white text-sm shadow-xs">
              LD
            </div>
            <div className="hidden sm:block">
              <span className="font-extrabold text-base tracking-tight text-slate-900 font-heading">
                LaunchDeck
              </span>
              <span className="text-sm text-indigo-600 font-bold ml-1.5">
                academy
              </span>
            </div>
          </Link>

          <div className="h-5 w-px bg-slate-200 hidden md:block shrink-0" />

          {/* Current Course Title */}
          <div className="hidden md:flex items-center gap-2.5 truncate">
            <span className="text-sm font-bold text-slate-800 truncate">
              {COURSE_DATA.title}
            </span>
            <span className="text-xs font-sans font-semibold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 shrink-0">
              Module 1.4
            </span>
          </div>
        </div>

        {/* Right Nav Actions */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <button
            onClick={() => setViewMode(viewMode === "player" ? "catalog" : "player")}
            className="text-sm font-semibold px-3.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-slate-700 flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4 text-indigo-600" />
            <span>{viewMode === "player" ? "All Courses" : "Return to Player"}</span>
          </button>

          <Link to="/">
            <Button variant="ghost" size="sm" className="h-9 text-sm font-medium text-slate-600 hover:text-slate-900 gap-1.5 hidden sm:inline-flex">
              <ArrowLeft className="w-4 h-4" /> Workspace
            </Button>
          </Link>

          <div className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-bold shadow-xs">
            JD
          </div>
        </div>
      </header>

      {/* ── CATALOG VIEW (When user clicks All Courses) ── */}
      {viewMode === "catalog" ? (
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-8 py-8 space-y-8">
          <div className="flex items-center justify-between border-b pb-4 border-slate-200">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Enrolled Programs & Academy Hub
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                Continue your learning tracks or review certified masterclasses.
              </p>
            </div>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs h-9 gap-1.5 shadow-xs"
              onClick={() => setViewMode("player")}
            >
              <Play className="w-3.5 h-3.5 fill-white" /> Continue LLM Course
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Primary Flagship Course */}
            <Card className="border border-indigo-200 bg-white rounded-2xl overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between">
              <div>
                <div className="h-44 w-full overflow-hidden relative">
                  <img src={COURSE_DATA.coverVideoThumbnail} alt={COURSE_DATA.title} className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3 px-2 py-0.5 rounded text-[10px] font-semibold bg-indigo-600 text-white">
                    Flagship Course
                  </div>
                  <div className="absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-medium bg-black/70 text-white backdrop-blur-xs">
                    {COURSE_DATA.totalDuration}
                  </div>
                </div>
                <div className="p-5 space-y-2">
                  <h3 className="font-bold text-base text-slate-900 leading-snug">{COURSE_DATA.title}</h3>
                  <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{COURSE_DATA.about}</p>
                  <div className="pt-2">
                    <div className="flex justify-between text-[11px] text-slate-500 mb-1">
                      <span>Progress</span>
                      <span className="font-bold text-indigo-600">{percentComplete}%</span>
                    </div>
                    <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-600 rounded-full transition-all duration-500" style={{ width: `${percentComplete}%` }} />
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-5 pt-0">
                <Button
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs h-9 gap-1.5 shadow-xs"
                  onClick={() => setViewMode("player")}
                >
                  <Play className="w-3.5 h-3.5 fill-white" /> Continue Learning
                </Button>
              </div>
            </Card>

            {/* Secondary Courses */}
            {SECONDARY_COURSES.map(sc => (
              <Card key={sc.id} className="border border-slate-200 bg-white rounded-2xl overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col justify-between">
                <div>
                  <div className="h-44 w-full overflow-hidden relative">
                    <img src={sc.cover} alt={sc.title} className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 px-2 py-0.5 rounded text-[10px] font-medium bg-black/70 text-white backdrop-blur-xs">
                      {sc.totalHours}
                    </div>
                  </div>
                  <div className="p-5 space-y-2">
                    <h3 className="font-bold text-base text-slate-900 leading-snug">{sc.title}</h3>
                    <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{sc.description}</p>
                    <div className="pt-2">
                      <div className="flex justify-between text-[11px] text-slate-500 mb-1">
                        <span>Progress</span>
                        <span className="font-bold text-emerald-600">{sc.progress}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${sc.progress}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-5 pt-0">
                  <Button
                    variant="outline"
                    className="w-full text-xs h-9 font-medium"
                    onClick={() => {
                      toast.info(`Switched active curriculum to ${sc.title}`);
                      setViewMode("player");
                    }}
                  >
                    Start Course
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </main>
      ) : (
        /* ── COURSE PLAYER & WORKSPACE (Exact Match to Dribbble Ref 15473206) ── */
        <main className="flex-1 max-w-[1560px] mx-auto w-full p-4 sm:p-6 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* ═══════════════════════════════════════════════════════════
                LEFT COLUMN (~68%): Video Player + Tabs + Details + Outcomes
            ═══════════════════════════════════════════════════════════ */}
            <div className="lg:col-span-8 space-y-6">
              {/* 1. Large Hero Video & Interactive Lecture Area (Enterprise Canvas DRM Engine) */}
              <div
                className="relative rounded-2xl overflow-hidden bg-slate-950 aspect-video shadow-md border border-slate-800/90 group select-none"
                onContextMenu={(e) => {
                  e.preventDefault();
                  toast.error("🔒 Video downloading, right-click, and inspection are strictly prohibited.");
                }}
              >
                {/* ── Strict Anti-Download / Anti-Inspect HTML5 Canvas Stream ── */}
                {/* Zero <video> tag, zero <source> tag, zero mp4 URL in DOM */}
                <canvas
                  ref={canvasRef}
                  width={1280}
                  height={720}
                  className="w-full h-full object-cover select-none pointer-events-none"
                />

                {/* Anti-Inspection Transparent Shield Layer (Intercepts DOM Inspector Clicks) */}
                <div
                  className="absolute inset-0 z-10 select-none cursor-pointer"
                  onClick={() => setIsPlaying(!isPlaying)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    toast.error("🔒 Right-click is disabled to protect copyrighted course content.");
                  }}
                  title={isPlaying ? "Click to Pause" : "Click to Play Lecture"}
                />

                {/* DevTools Open Security Alert Overlay */}
                {devToolsActive && (
                  <div className="absolute inset-0 z-30 bg-slate-950/95 backdrop-blur-md flex flex-col items-center justify-center p-8 text-center space-y-3">
                    <div className="w-14 h-14 rounded-full bg-red-500/20 text-red-400 border border-red-500/40 flex items-center justify-center shadow-lg">
                      <ShieldAlert className="w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-bold text-white">Protected Content Paused</h3>
                    <p className="text-sm text-slate-300 max-w-md">
                      Developer tools or screen inspection detected. To safeguard proprietary lecture material, video streaming is halted. Please close the inspection panel to resume your lesson.
                    </p>
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDevToolsActive(false);
                      }}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs mt-2"
                    >
                      Resume Learning
                    </Button>
                  </div>
                )}

                {/* Top Floating Controls: DRM Status & Playback Settings (No Download Allowed) */}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-20 pointer-events-none">
                  {/* Left: Security Badges */}
                  <div className="flex items-center gap-2 pointer-events-auto">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/70 backdrop-blur-md border border-emerald-500/50 text-emerald-400 text-xs font-semibold shadow-xs">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                      <span>DRM Encrypted Stream</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    </div>
                    <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-black/60 backdrop-blur-md border border-slate-700/60 text-slate-300 text-xs font-sans">
                      <Lock className="w-3 h-3 text-indigo-400" />
                      <span>Watermark: {drmTicket?.anti_tamper_watermark?.session_id || "ID-8921"}</span>
                    </div>
                  </div>

                  {/* Right: Bookmark & Speed Settings */}
                  <div className="flex items-center gap-2 pointer-events-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsBookmarked(!isBookmarked);
                        toast.success(isBookmarked ? "Removed from bookmarks" : "Lesson saved to bookmarks!");
                      }}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs backdrop-blur-xs transition-colors ${
                        isBookmarked ? "bg-amber-500 text-white" : "bg-black/60 hover:bg-black/80 text-white"
                      }`}
                      title="Save to bookmarks"
                    >
                      <Bookmark className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const speeds = ["1.0x", "1.25x", "1.5x", "2.0x"];
                        const next = speeds[(speeds.indexOf(playbackSpeed) + 1) % speeds.length];
                        setPlaybackSpeed(next);
                        toast.info(`Playback speed: ${next}`);
                      }}
                      className="px-2.5 h-8 rounded-lg bg-black/60 hover:bg-black/80 text-white flex items-center justify-center text-xs font-sans font-semibold backdrop-blur-xs transition-colors"
                      title="Playback Speed"
                    >
                      {playbackSpeed}
                    </button>
                  </div>
                </div>

                {/* Center Large Elegant Play / Pause Button */}
                {!isPlaying && !devToolsActive && (
                  <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsPlaying(true);
                      }}
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/90 hover:bg-white text-slate-950 flex items-center justify-center shadow-2xl transition-transform hover:scale-105 pointer-events-auto"
                      title="Play Lecture"
                    >
                      <Play className="w-7 h-7 sm:w-8 sm:h-8 fill-slate-950 text-slate-950 ml-1" />
                    </button>
                  </div>
                )}

                {/* Bottom Video Scrub Bar & Controls */}
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 text-white z-20 space-y-2 pointer-events-auto">
                  <div
                    className="w-full bg-white/20 hover:bg-white/30 h-1.5 rounded-full overflow-hidden cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const pos = (e.clientX - rect.left) / rect.width;
                      setProgressSec(Math.floor(pos * 1100));
                    }}
                  >
                    <div className="bg-indigo-500 h-full rounded-full transition-all" style={{ width: `${(progressSec / 1100) * 100}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-xs text-white/90">
                    <div className="flex items-center gap-3">
                      <button onClick={() => setIsPlaying(!isPlaying)} className="hover:text-white" title={isPlaying ? "Pause" : "Play"}>
                        {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <button onClick={() => setIsMuted(!isMuted)} className="hover:text-white" title={isMuted ? "Unmute" : "Mute"}>
                        {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                      </button>
                      <span className="font-sans text-[11px] text-white/80">
                        {Math.floor(progressSec / 60).toString().padStart(2, "0")}:{Math.floor(progressSec % 60).toString().padStart(2, "0")} / {currentLesson.videoTime}
                      </span>
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span className="text-[10px] font-sans font-bold uppercase tracking-wider bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 px-2 py-0.5 rounded">
                        DRM 1080p HD
                      </span>
                      <Maximize2
                        className="w-4 h-4 cursor-pointer hover:text-white"
                        onClick={() => {
                          if (!document.fullscreenElement) {
                            canvasRef.current?.parentElement?.requestFullscreen?.();
                          } else {
                            document.exitFullscreen?.();
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Sub-Navigation Tabs Directly Under Video Player (Dribbble Layout) */}
              <div className="border-b border-slate-200 flex items-center gap-8 text-base font-semibold overflow-x-auto scrollbar-none">
                {[
                  { id: "overview", label: "Overview" },
                  { id: "outcomes", label: "Outcomes & Syllabus" },
                  { id: "qa", label: "Q&A & AI Tutor" },
                  { id: "notes", label: "Notes & Downloads" },
                  { id: "announcements", label: "Announcements" },
                ].map(tab => {
                  const active = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`pb-3.5 px-1 transition-all whitespace-nowrap border-b-2 text-base ${
                        active
                          ? "border-indigo-600 text-indigo-600 font-bold"
                          : "border-transparent text-slate-500 hover:text-slate-900"
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* 3. Instructor Row + 4 Key Meta Stats (Exact Dribbble Layout) */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                <div className="flex items-center gap-3.5">
                  <img
                    src={COURSE_DATA.instructor.avatar}
                    alt={COURSE_DATA.instructor.name}
                    className="w-12 h-12 rounded-full object-cover border border-slate-200 shrink-0"
                  />
                  <div>
                    <h4 className="font-bold text-base text-slate-900">{COURSE_DATA.instructor.name}</h4>
                    <p className="text-sm text-slate-500 font-medium">{COURSE_DATA.instructor.role}</p>
                  </div>
                </div>

                {/* 4 Stats in clean horizontal row */}
                <div className="flex items-center gap-5 sm:gap-7 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <div>
                      <span className="font-bold text-base text-slate-900">{COURSE_DATA.rating}</span>
                      <span className="text-xs text-slate-500 ml-1.5 hidden sm:inline">Rating</span>
                    </div>
                  </div>
                  <div className="h-5 w-px bg-slate-200" />
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-500" />
                    <div>
                      <span className="font-bold text-base text-slate-900">{COURSE_DATA.studentsCount}</span>
                      <span className="text-xs text-slate-500 ml-1.5 hidden sm:inline">Students</span>
                    </div>
                  </div>
                  <div className="h-5 w-px bg-slate-200" />
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <div>
                      <span className="font-bold text-base text-slate-900">{COURSE_DATA.totalDuration}</span>
                      <span className="text-xs text-slate-500 ml-1.5 hidden sm:inline">Total</span>
                    </div>
                  </div>
                  <div className="h-5 w-px bg-slate-200" />
                  <div className="flex items-center gap-2">
                    <FolderDown className="w-4 h-4 text-slate-500" />
                    <div>
                      <span className="font-bold text-base text-slate-900">{COURSE_DATA.totalFiles}</span>
                      <span className="text-xs text-slate-500 ml-1.5 hidden sm:inline">Files</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 4. Course Title & Active Lesson Header */}
              <div className="space-y-2 pt-2">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-snug">
                  {COURSE_DATA.title}
                </h2>
                <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                  <span>Last Updated: {COURSE_DATA.lastUpdated}</span>
                  <span>·</span>
                  <span>{COURSE_DATA.language}</span>
                  <span>·</span>
                  <span className="text-emerald-600 font-semibold flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Enrolled & Verified
                  </span>
                </div>
              </div>

              {/* ═══════════════════════════════════════════════════════════
                  5. TAB CONTENT: OVERVIEW (Coursera + Dribbble Style)
              ═══════════════════════════════════════════════════════════ */}
              {activeTab === "overview" && (
                <div className="space-y-8 pt-3">
                  {/* About This Course */}
                  <div className="space-y-3">
                    <h3 className="font-bold text-lg text-slate-900">About This Course</h3>
                    <p className="text-base text-slate-700 leading-relaxed font-sans">
                      {COURSE_DATA.about}
                    </p>
                  </div>

                  {/* What You'll Learn (Outcomes - Coursera Style) */}
                  <div className="rounded-2xl border border-slate-200/90 bg-white p-6 sm:p-7 space-y-4 shadow-xs">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-sm">
                        ✓
                      </div>
                      <h3 className="font-bold text-lg text-slate-900">What You'll Learn (Core Outcomes)</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-800 font-medium">
                      {COURSE_DATA.outcomes.map((outcome, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span className="leading-relaxed">{outcome}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Skills You'll Gain (Coursera Style) */}
                  <div className="space-y-3">
                    <h3 className="font-bold text-lg text-slate-900">Skills You'll Gain</h3>
                    <div className="flex flex-wrap gap-2.5">
                      {COURSE_DATA.skills.map((skill, idx) => (
                        <span
                          key={idx}
                          className="px-3.5 py-1.5 rounded-full text-sm font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200/80 shadow-xs"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Course Supplements / Downloads (Exact Dribbble Style) */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-slate-900">Course Supplements</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {COURSE_DATA.supplements.map(file => (
                        <Card
                          key={file.id}
                          className="p-5 border border-slate-200 rounded-xl bg-white hover:border-indigo-400 transition-all flex flex-col justify-between shadow-xs space-y-3.5"
                        >
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <Badge variant="outline" className="text-xs text-indigo-600 border-indigo-200 bg-indigo-50/50">{file.type}</Badge>
                              <span className="text-xs font-sans text-slate-400">{file.size}</span>
                            </div>
                            <h4 className="font-bold text-sm text-slate-900 line-clamp-2 leading-snug">{file.title}</h4>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full text-sm h-9 gap-1.5 font-semibold border-slate-200 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200"
                            onClick={() => handleDownloadSupplement(file)}
                          >
                            <Download className="w-4 h-4" /> Download
                          </Button>
                        </Card>
                      ))}
                    </div>
                  </div>

                  {/* Recommended Next Courses (Coursera Style) */}
                  <div className="space-y-4">
                    <h3 className="font-bold text-lg text-slate-900">Recommended Next Courses</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {COURSE_DATA.recommendations.map(rec => (
                        <Card key={rec.id} className="p-4 sm:p-5 border border-slate-200 rounded-xl bg-white flex items-center gap-4 shadow-xs hover:border-indigo-300 transition-all">
                          <img src={rec.cover} alt={rec.title} className="w-24 h-24 rounded-lg object-cover shrink-0" />
                          <div className="space-y-1.5 min-w-0">
                            <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider">{rec.tag}</span>
                            <h4 className="font-bold text-sm text-slate-900 truncate leading-snug">{rec.title}</h4>
                            <div className="flex items-center gap-2.5 text-xs text-slate-500 font-medium">
                              <span className="flex items-center gap-1 text-amber-500 font-bold"><Star className="w-3.5 h-3.5 fill-amber-400" /> {rec.rating}</span>
                              <span>·</span>
                              <span>{rec.students} students</span>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ═══════════════════════════════════════════════════════════
                  TAB CONTENT: OUTCOMES & MODULES (Coursera Accordion)
              ═══════════════════════════════════════════════════════════ */}
              {activeTab === "outcomes" && (
                <div className="space-y-6 pt-3">
                  <div className="space-y-1.5">
                    <h3 className="font-bold text-lg text-slate-900">Comprehensive Curriculum Breakdown</h3>
                    <p className="text-sm text-slate-500">4 modules, 10 lessons, interactive code notebooks, and automated verification quizzes.</p>
                  </div>

                  <div className="space-y-4">
                    {COURSE_DATA.modules.map((mod, i) => (
                      <Card key={mod.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
                        <div className="p-4 sm:p-5 bg-slate-50/70 border-b border-slate-200 flex items-center justify-between">
                          <div>
                            <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider">Module {i + 1}</span>
                            <h4 className="font-bold text-base text-slate-900 mt-1">{mod.title}</h4>
                          </div>
                          <span className="text-sm font-sans font-medium text-slate-500">{mod.duration}</span>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {mod.lessons.map(les => {
                            const isCurrent = les.id === activeLessonId;
                            const isDone = completedLessons.includes(les.id);
                            return (
                              <div
                                key={les.id}
                                onClick={() => handleSelectLesson(les)}
                                className={`p-4 sm:p-5 flex items-start justify-between gap-4 cursor-pointer transition-colors ${
                                  isCurrent ? "bg-indigo-50/50" : "hover:bg-slate-50/70"
                                }`}
                              >
                                <div className="flex items-start gap-3.5">
                                  <div className="mt-0.5">
                                    {isDone ? (
                                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                    ) : (
                                      <Play className="w-5 h-5 text-indigo-600" />
                                    )}
                                  </div>
                                  <div className="space-y-1">
                                    <h5 className="font-bold text-sm text-slate-900">{les.title}</h5>
                                    <p className="text-sm text-slate-600 leading-relaxed">{les.description}</p>
                                  </div>
                                </div>
                                <span className="text-xs font-sans text-slate-500 shrink-0 font-medium">{les.duration}</span>
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* ═══════════════════════════════════════════════════════════
                  TAB CONTENT: Q&A & AI TUTOR (Interactive Gemini Assistant)
              ═══════════════════════════════════════════════════════════ */}
              {activeTab === "qa" && (
                <div className="space-y-6 pt-3">
                  <div className="flex items-center justify-between border-b pb-3.5 border-slate-200">
                    <div>
                      <h3 className="font-bold text-lg text-slate-900 flex items-center gap-2.5">
                        <Bot className="w-6 h-6 text-indigo-600" />
                        <span>Interactive AI Course Tutor & Discussion</span>
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5">Ask any technical question regarding {currentLesson.title}. Answers generated via Gemini Flash.</p>
                    </div>
                    <Badge variant="outline" className="text-xs font-semibold text-emerald-700 border-emerald-300 bg-emerald-50 px-3 py-1">
                      Tutor Online
                    </Badge>
                  </div>

                  <div className="space-y-4 max-h-[480px] overflow-y-auto pr-2">
                    {qaMessages.map(msg => (
                      <div
                        key={msg.id}
                        className={`p-4 sm:p-5 rounded-xl border text-sm space-y-2 ${
                          msg.role === "student"
                            ? "bg-indigo-50/70 border-indigo-200 ml-8 text-slate-900"
                            : "bg-white border-slate-200 mr-8 text-slate-800 shadow-xs"
                        }`}
                      >
                        <div className="flex items-center justify-between font-bold text-xs">
                          <span className={msg.role === "assistant" ? "text-indigo-600" : msg.role === "instructor" ? "text-purple-600" : "text-slate-900"}>
                            {msg.sender}
                          </span>
                          <span className="text-slate-400 font-normal">{msg.time}</span>
                        </div>
                        <p className="leading-relaxed font-sans">{msg.text}</p>
                      </div>
                    ))}
                  </div>

                  {/* Input form */}
                  <form onSubmit={handleAskTutor} className="flex items-center gap-3 pt-2">
                    <Input
                      value={userQuestion}
                      onChange={(e) => setUserQuestion(e.target.value)}
                      placeholder={`Ask Dr. Andrew Lin or the AI Course Tutor about ${currentLesson.title}...`}
                      className="text-sm h-11 bg-white border-slate-300 placeholder:text-slate-400"
                    />
                    <Button type="submit" disabled={askingAi} className="h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm px-5 shrink-0 gap-2 shadow-xs">
                      {askingAi ? "Thinking…" : <><Send className="w-4 h-4" /> Ask Tutor</>}
                    </Button>
                  </form>
                </div>
              )}

              {/* ═══════════════════════════════════════════════════════════
                  TAB CONTENT: NOTES & CODE (Downloadable Resources)
              ═══════════════════════════════════════════════════════════ */}
              {activeTab === "notes" && (
                <div className="space-y-6 pt-3">
                  <div className="space-y-1">
                    <h3 className="font-bold text-lg text-slate-900">Lesson Code & Mathematical Notes</h3>
                    <p className="text-sm text-slate-500">Live implementation for {currentLesson.title}.</p>
                  </div>

                  <div className="rounded-xl overflow-hidden border border-slate-800 bg-slate-950 text-emerald-400 font-sans text-sm shadow-md">
                    <div className="p-3.5 bg-slate-900 border-b border-slate-800 flex items-center justify-between text-slate-300 text-xs font-semibold">
                      <span>{currentLesson.title.toLowerCase().replace(/[^a-z0-9]/g, "_")}.py</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs text-slate-300 hover:text-white"
                        onClick={() => {
                          navigator.clipboard.writeText(currentLesson.code);
                          toast.success("Code snippet copied!");
                        }}
                      >
                        Copy Code
                      </Button>
                    </div>
                    <pre className="p-4 sm:p-5 overflow-x-auto leading-relaxed">{currentLesson.code}</pre>
                  </div>
                </div>
              )}

              {/* ═══════════════════════════════════════════════════════════
                  TAB CONTENT: ANNOUNCEMENTS
              ═══════════════════════════════════════════════════════════ */}
              {activeTab === "announcements" && (
                <div className="space-y-4 pt-3">
                  <Card className="p-6 border border-slate-200 rounded-xl bg-white space-y-2.5 shadow-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-bold text-indigo-600">Specialization Update · October 2026</span>
                      <span className="text-xs text-slate-400">3 days ago</span>
                    </div>
                    <h4 className="font-bold text-base text-slate-900">Module 4: Multi-Agent Tool Calling Live Update</h4>
                    <p className="text-sm text-slate-700 leading-relaxed font-sans">
                      We have updated Lesson 9 and 10 with the latest Gemini 3.8 Live API tool-use schemas and asynchronous function calling loops. Check out the updated JSON schemas in the Downloads tab!
                    </p>
                  </Card>
                </div>
              )}
            </div>

            {/* ═══════════════════════════════════════════════════════════
                RIGHT COLUMN (~32%): Course Content / Syllabus (Exact Dribbble layout!)
            ═══════════════════════════════════════════════════════════ */}
            <div className="lg:col-span-4 sticky top-24 space-y-4">
              <Card className="rounded-2xl border border-slate-200/90 bg-white p-5 sm:p-6 shadow-xs space-y-5">
                {/* Header: Course Content + Progress */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg text-slate-900">Course Content</h3>
                    <span className="text-sm font-semibold text-slate-500">
                      {completedLessons.length} / {allLessons.length} Completed
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="space-y-1.5">
                    <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="bg-indigo-600 h-full rounded-full transition-all duration-500"
                        style={{ width: `${percentComplete}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-slate-500 font-medium">
                      <span>Overall Progress</span>
                      <span className="font-bold text-indigo-600">{percentComplete}%</span>
                    </div>
                  </div>
                </div>

                {/* Lesson List (Matching Dribbble visual hierarchy exactly) */}
                <div className="space-y-1.5 max-h-[640px] overflow-y-auto pr-1">
                  {allLessons.map((les) => {
                    const isActive = les.id === activeLessonId;
                    const isDone = completedLessons.includes(les.id);

                    return (
                      <div
                        key={les.id}
                        onClick={() => handleSelectLesson(les)}
                        className={`group p-3 sm:p-3.5 rounded-xl flex items-center justify-between gap-3 cursor-pointer transition-all border ${
                          isActive
                            ? "bg-indigo-50/80 border-indigo-200 text-indigo-950 font-bold shadow-xs"
                            : "border-transparent hover:bg-slate-50 hover:border-slate-200 text-slate-700"
                        }`}
                      >
                        {/* Left: Icon + Title */}
                        <div className="flex items-center gap-3 min-w-0">
                          {/* Circular Status Icon */}
                          <button
                            type="button"
                            onClick={(e) => handleToggleComplete(les.id, e)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-transform group-hover:scale-105 ${
                              isDone
                                ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                                : isActive
                                ? "bg-indigo-600 text-white shadow-xs"
                                : "bg-slate-100 text-slate-400 border border-slate-200"
                            }`}
                            title={isDone ? "Mark incomplete" : "Mark completed"}
                          >
                            {isDone ? (
                              <Check className="w-4 h-4 stroke-[2.5]" />
                            ) : isActive ? (
                              <Play className="w-3.5 h-3.5 fill-white ml-0.5" />
                            ) : (
                              <Play className="w-3 h-3 ml-0.5 text-slate-400" />
                            )}
                          </button>

                          <div className="truncate">
                            <span className={`text-sm block truncate leading-tight ${isActive ? "text-indigo-950 font-bold" : "text-slate-900 font-semibold"}`}>
                              {les.title}
                            </span>
                            <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                              <span>⏱ {les.duration}</span>
                              {isDone && <span className="text-emerald-600 font-semibold">· Completed</span>}
                              {isActive && <span className="text-indigo-600 font-semibold animate-pulse">· Playing Now</span>}
                            </div>
                          </div>
                        </div>

                        {/* Right: Duration pill */}
                        <span className={`text-xs font-sans shrink-0 font-medium ${isActive ? "text-indigo-600 font-bold" : "text-slate-400"}`}>
                          {les.duration}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Mark Lesson Complete Action Button */}
                <div className="pt-3 border-t border-slate-100 space-y-2.5">
                  <Button
                    className={`w-full text-sm font-semibold h-11 gap-2 shadow-xs transition-colors ${
                      completedLessons.includes(activeLessonId)
                        ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        : "bg-emerald-600 hover:bg-emerald-700 text-white"
                    }`}
                    onClick={(e) => handleToggleComplete(activeLessonId, e)}
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>
                      {completedLessons.includes(activeLessonId)
                        ? "Completed (Click to Undo)"
                        : "Mark Lesson Complete ✓"}
                    </span>
                  </Button>

                  <div className="text-center">
                    <span className="text-xs text-slate-400 font-medium">
                      Earns verified course certificate upon 100% completion
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </main>
      )}

      {/* ══════════════════════════════════════════════════════════════
          MODAL: KNOWLEDGE VERIFICATION TEST (Before Completion)
      ══════════════════════════════════════════════════════════════ */}
      <Dialog open={quizOpen} onOpenChange={setQuizOpen}>
        <DialogContent className="sm:max-w-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
          <DialogHeader>
            <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 text-xs font-bold uppercase tracking-wider">
              <Award className="w-4 h-4" />
              <span>Prerequisite Knowledge Check</span>
            </div>
            <DialogTitle className="text-lg font-bold">
              {quizLesson?.title || "Lesson Quiz"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Verify your architectural understanding to unlock verified completion credits and XP.
            </DialogDescription>
          </DialogHeader>

          {quizLesson && (
            <div className="py-2 space-y-4">
              <div className="p-4 rounded-xl bg-indigo-50/70 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900/50">
                <p className="text-sm font-semibold text-slate-900 dark:text-white leading-relaxed">
                  {getLessonQuiz(quizLesson).question}
                </p>
              </div>

              {/* Options */}
              <div className="space-y-2.5">
                {getLessonQuiz(quizLesson).options.map((opt, idx) => {
                  const isSelected = selectedOption === idx;
                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        setSelectedOption(idx);
                        setQuizError("");
                      }}
                      className={`p-3.5 rounded-xl border cursor-pointer text-xs font-medium transition-all flex items-start gap-3 ${
                        isSelected
                          ? "bg-indigo-600 text-white border-indigo-600 shadow-xs"
                          : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-indigo-400"
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center font-bold text-[10px] shrink-0 mt-0.5 ${
                        isSelected ? "bg-white text-indigo-600" : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </div>
                      <span className="leading-relaxed flex-1">{opt}</span>
                    </div>
                  );
                })}
              </div>

              {quizError && (
                <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-xs flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{quizError}</span>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" size="sm" onClick={() => setQuizOpen(false)} className="text-xs">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleVerifyQuiz}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs h-9 px-5 gap-1.5 shadow-xs"
            >
              <Check className="w-4 h-4" /> Submit & Verify Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════════
          MODAL: AUTOMATIC CERTIFICATE SYSTEM (2-Year Validity + Scannable QR)
      ══════════════════════════════════════════════════════════════ */}
      <Dialog open={certModalOpen} onOpenChange={setCertModalOpen}>
        <DialogContent className="sm:max-w-3xl bg-[#0B101B] border border-slate-800 text-white p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2 text-emerald-400 text-xs font-sans font-bold tracking-widest uppercase">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span>Specialization Completed · 2-Year Official Credential</span>
            </div>
            <DialogTitle className="text-xl sm:text-2xl font-extrabold text-white">
              Official Verified Specialization Certificate
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400">
              Your completion is cryptographically registered with LaunchDeck & Colibri AI Labs.
            </DialogDescription>
          </DialogHeader>

          {issuedCert && (
            <div className="space-y-6 py-2">
              {/* Certificate Canvas Card */}
              <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-b from-[#111827] to-[#0A0E17] p-6 sm:p-8 relative shadow-2xl overflow-hidden">
                <div className="absolute inset-2 border border-amber-500/20 rounded-xl pointer-events-none" />

                <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4 relative z-10">
                  <div className="space-y-1 text-center sm:text-left">
                    <span className="text-[10px] font-sans font-bold tracking-widest uppercase text-amber-400">
                      CREDENTIAL SERIAL: {issuedCert.id}
                    </span>
                    <h3 className="text-xs font-semibold uppercase text-slate-300">
                      Colibri AI Labs · Executive Academy
                    </h3>
                  </div>

                  {/* Scannable QR Code */}
                  <div className="flex flex-col items-center gap-1 bg-black/60 p-2.5 rounded-xl border border-slate-800 shrink-0">
                    <img
                      src={issuedCert.qr_code}
                      alt="Scan to Verify"
                      className="w-20 h-20 bg-white p-1 rounded-lg"
                    />
                    <span className="text-[8px] font-sans text-slate-400">SCAN TO VERIFY</span>
                  </div>
                </div>

                {/* Recipient & Heading 2 Link with Montserrat 22px */}
                <div className="text-center py-6 sm:py-8 space-y-2 relative z-10">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-sans">This certifies that</p>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                    {issuedCert.recipient}
                  </h2>
                  <p className="text-xs text-slate-400">has fulfilled all curriculum competencies and automated quizzes for</p>
                  
                  {/* Required Montserrat Size 22, Italic 0, Weight 400 Heading 2 Link */}
                  <div className="pt-2">
                    <Link
                      to="/portal/colibri-client"
                      className="heading-link font-['Montserrat',sans-serif] text-[22px] font-normal not-italic text-amber-300 hover:text-amber-200 transition-colors inline-flex items-center gap-1.5 underline underline-offset-4 decoration-amber-400/40"
                    >
                      <span>{issuedCert.title}</span>
                      <ExternalLink className="w-4 h-4 opacity-75 shrink-0" />
                    </Link>
                  </div>
                </div>

                {/* Strict 2-Year Validity & Signatures */}
                <div className="pt-4 border-t border-slate-800 text-xs text-slate-400 grid grid-cols-1 sm:grid-cols-2 gap-4 relative z-10">
                  <div className="space-y-1">
                    <div className="text-emerald-400 font-semibold flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Issued: {issuedCert.issue_date}</span>
                    </div>
                    <div className="text-amber-300 font-semibold flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>Strict Expiration: {issuedCert.expiry_date} (2 Years)</span>
                    </div>
                  </div>
                  <div className="sm:text-right space-y-0.5">
                    <span className="font-serif italic text-amber-200 text-base">Dr. Andrew Lin</span>
                    <p className="text-[10px] text-slate-500 font-sans">Lead AI Faculty · Stanford & Colibri AI Labs</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <Link to={`/verify/${issuedCert.id}`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="text-xs border-slate-700 bg-slate-900 text-slate-200 hover:text-white gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5 text-indigo-400" /> Open Live Public Registry
                  </Button>
                </Link>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs border-slate-700 bg-slate-900 text-slate-200 hover:text-white gap-1.5"
                    onClick={() => {
                      navigator.clipboard.writeText(issuedCert.verification);
                      toast.success("Verification URL copied to clipboard!");
                    }}
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy Verify Link
                  </Button>
                  <Button
                    size="sm"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs gap-1.5 shadow-xs"
                    onClick={() => {
                      toast.success("Opening print / PDF download dialog...");
                      window.print();
                    }}
                  >
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
